import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../../../config";
import "./CryptoPayment.css";

import usdtQR from "../../../assets/images/usdt.jpeg";
import bitcoinQR from "../../../assets/images/bitcoin.jpeg";
import ethereumQR from "../../../assets/images/ethereum.jpeg";
import usdcQR from "../../../assets/images/usdc.jpeg";

import { Helmet } from "react-helmet-async";

function CryptoPayment() {

  const { coin } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const [cartItems, setCartItems] = useState(
    JSON.parse(localStorage.getItem("cart")) || []
  );

  const shippingData = JSON.parse(localStorage.getItem("shippingData")) || {};
  const medicalData = JSON.parse(localStorage.getItem("medicalData")) || {};

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Same guard as BankDetails — don't show a broken page on empty cart.
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate("/checkout");
    }
  }, [cartItems, navigate]);

  // Wallet address, network label, and the matching static QR image
  // for each supported coin.
  const wallets = {
    usdt: {
      address: "TMfjZpwLFrEfjDvsiSVHHmhn43P9kPhGsS",
      network: "TRC20",
      qr: usdtQR
    },
    bitcoin: {
      address: "0x75acd0517ad95a0557ccfdcf9acf26cb71931fd1",
      network: "BEP20",
      qr: bitcoinQR
    },
    ethereum: {
      address: "0x75acd0517ad95a0557ccfdcf9acf26cb71931fd1",
      network: "BEP20",
      qr: ethereumQR
    },
    usdc: {
      address: "0x75acd0517ad95a0557ccfdcf9acf26cb71931fd1",
      network: "BEP20",
      qr: usdcQR
    }
  };


  const coinLabels = {
    usdt: "Tether (USDT)",
    bitcoin: "Bitcoin (BTC)",
    ethereum: "Ethereum (ETH)",
    usdc: "USD Coin (USDC)"
  };

  const walletInfo = wallets[coin];

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const shipping = cartItems.length > 0 ? 25 : 0;
  const discount = subtotal * 0.1; // crypto discount
  const total = subtotal + shipping - discount;

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  // Order is created here — only after the user clicks "I Have Paid".
  const confirmPayment = async () => {
    if (cartItems.length === 0) {
      alert("Cart is empty");
      return;
    }

    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(
        `${BACKEND_URL}/api.php?module=orders&action=create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user?.id,
            shipping: shippingData,
            medical: medicalData,
            paymentMethod: `Crypto (${coin.toUpperCase()})`,
            cart: cartItems,
            subtotal,
            shippingCharge: shipping,
            discount,
            total,
            status: "payment_pending_confirmation"
          })
        }
      );

      const data = await response.json();

      if (data.success) {

        const realReference =
          `ORD-${String(data.order_id).padStart(6, "0")}`;

        const orderSnapshot = {
          cart: cartItems,
          shippingData,
          medicalData,
          paymentMethod: `Crypto (${coin.toUpperCase()})`,
          subtotal,
          shipping,
          discount,
          total,
          invoiceNumber: realReference,
          orderId: data.order_id,
          orderDate: new Date().toISOString()
        };

        localStorage.setItem("lastOrder", JSON.stringify(orderSnapshot));

        localStorage.removeItem("cart");
        localStorage.removeItem("shippingData");
        localStorage.removeItem("medicalData");
        localStorage.removeItem("paymentMethod");

        window.dispatchEvent(new Event("cartUpdated"));

        navigate("/success");
      } else {
        alert(data.message || "Order failed");
      }
    } catch (err) {
      alert("Server error ");
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return null;
  }

  if (!walletInfo) {
    return (
      <div className="crypto-payment-page">
        <div className="crypto-payment-box">
          <h1>Invalid payment method</h1>
          <button className="paid-btn" onClick={() => navigate("/checkout/crypto-selection")}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (

    <>
     <Helmet>
            <title>Bank Transfer Payment</title>
            <link rel="canonical" href="https://kamagrastar.eu/crypto-payment" />
      </Helmet>

    <div className="crypto-payment-page">

      <div className="crypto-payment-box">

        <p className="crypto-instructions">
          To complete the payment of €{total.toFixed(2)}, please scan the QR code
          or send {coinLabels[coin]} to the address below.
          Please use the {walletInfo.network} network.
        </p>

        <div className="crypto-copy-row">
          <span className="crypto-copy-label">Copy address</span>
          <div className="crypto-copy-box">
            <span className="crypto-copy-value">{walletInfo.address}</span>
            <button
              className="crypto-copy-btn"
              onClick={() => handleCopy(walletInfo.address, "address")}
            >
              {copiedField === "address" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        <div className="qr-box">
          <img src={walletInfo.qr} alt={`${coin} QR code`} />
        </div>

        <p className="crypto-recipient-info">
          Recipient information:{" "}
          {shippingData.address ? `${shippingData.address}, ` : ""}
          {shippingData.city ? `${shippingData.city}, ` : ""}
          {shippingData.country || ""}
        </p>

        <div className="crypto-total-box">
          <p>Total to pay: <strong>€{total.toFixed(2)}</strong></p>
        </div>

        <button
          className="paid-btn"
          onClick={confirmPayment}
          disabled={loading}
        >
          {loading ? "Processing..." : "I Have Paid"}
        </button>

      </div>

    </div>
    </>
  );
}

export default CryptoPayment; 